import { CompanyContext } from "contexts/CompanyContext";
import { AIContext } from "contexts/AIContext";
import { useContext } from "react"
import { getPexelsImages, getGiphyImages, getUnsplashImages, getPixabayImages } from "utils/stockImages";
import useRandomContexts from "./useRandomContexts";
import { getNetworkValidationIndex } from "./usePostValidator";
import {
  buildCreativeImagePromptMeta,
  cleanGeneratedPrompt,
  PROMPT_GENERATION_SCHEMA,
} from "utils/creativeStudioPrompt";

export const aiImageSizeByNetwork = {
  "instagram_feed": ["1:1", "4:3"],
  "instagram_reel": ["1:1", "16:9"],
  "instagram_story": ["9:16"],
  "facebook_feed": ["1:1", "4:3", "16:9"],
  "facebook_reel": ["1:1", "16:9"],
  "facebook_story": ["9:16"],
  "twitter_feed": ["1:1", "4:3", "16:9"],
  "linkedin_feed": ["1:1", "16:9"],
  "tiktok_feed": ["9:16"],
  "medium_feed": ["1:1", "3:2", "4:3", "16:9"],
  "pinterest_feed": ["1:1", "2:3", "3:4"],
  "threads_feed": ["1:1", "16:9"],
}

export const orientationByNetwork = {
  "instagram_feed": ["square", "portrait"],
  "instagram_reel": ["square", "portrait"],
  "instagram_story": ["portrait"],
  "facebook_feed": ["square", "portrait"],
  "facebook_reel": ["square", "portrait"],
  "facebook_story": ["portrait"],
  "twitter_feed": ["square", "portrait"], // actually landscape but we don't have templates
  "linkedin_feed": ["square", "portrait"],
  "tiktok_feed": ["portrait"],
  "medium_feed": ["square"], // actually landscape but we don't have templates
  "pinterest_feed": ["square", "portrait"],
  "threads_feed": ["square", "portrait"],
}

export const getOrientationByNetworkMediaProductType = (network, media_product_type = undefined) => {
  const full = orientationByNetwork?.[getNetworkValidationIndex(network, media_product_type)]?.random();
  return full;
}

export const getOrientationsByNetworkMediaProductType = (network, media_product_type = undefined) => {
  const full = orientationByNetwork?.[getNetworkValidationIndex(network, media_product_type)];
  return full;
}

export const getAIOrientationByNetworkMediaProductType = (network, media_product_type = undefined) => {
  const full = aiImageSizeByNetwork?.[getNetworkValidationIndex(network, media_product_type)]?.random();
  return full;
}

export const useCreativeGenerator = () => {

  const { company } = useContext(CompanyContext);
  const { getByQueryChat, getImages, getLastHistoryAIImageURL, getDriverModel, aiModels, aiImageModels, getVideos } = useContext(AIContext);
  const { generateRandomAIImagePrompts, generateRandomAIVideoPrompts } = useRandomContexts();

  const getStockImageDescription = async (copy) => {
    const prompt = `

      ${company?.name} POST COPY:
      ${copy}

      A creative agency needs to select a stock photo from Unsplash to post with the given POST COPY.
      Write a short image description to help them find the required stock image.

      INSTRUCTIONS:
      - Be creative
      - Relate the image description with the given POST COPY
      - Image description must be 3 words long or shorter
      - Do not use company or brand name

      Image description:
    `;
    const response = await getByQueryChat(prompt, false);
    let imageDescription = response.trim();
    return imageDescription;
  }

  const getAIImageDescription = async (copy) => {

    const { model: activeImageModelKey } = getDriverModel("image") || {};
    const activeImageModel = aiImageModels?.find(m => m?.model === activeImageModelKey);
    const useCreativeStudioPrompt = !!activeImageModel?.best;

    let imageDescription;

    if (useCreativeStudioPrompt) {
      const meta = buildCreativeImagePromptMeta({
        idea: copy?.split("\n")?.join(". "),
        useBrand: true,
        companyName: company?.name,
      });
      try {
        const response = await getByQueryChat(
          meta,
          false,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          PROMPT_GENERATION_SCHEMA
        );
        imageDescription = cleanGeneratedPrompt(response);
      } catch (err) {
        imageDescription = '';
      }
    }

    if (!imageDescription) {
      const imagePrompts = await generateRandomAIImagePrompts(1, false, copy?.split("\n")?.join(". "));
      const response = imagePrompts?.random();
      imageDescription = response?.trim() || '';
    }

    const style = company?.ai_image_styles?.length
      ? company?.ai_image_styles?.random()
      : null;
    if (style) {
      imageDescription = `${style != "No style" ? `${style} image: ` : ``}${imageDescription}.`;
    }

    return imageDescription;
  }

  const getAIVideoDescription = async (copy) => {

    const { model: _model } = getDriverModel("video");
    const modelObj = aiModels?.find(e => e?.model == _model);

    const videoPrompts = await generateRandomAIVideoPrompts(
      1,
      false,
      copy?.split("\n")?.join(". "),
      null,
      modelObj?.generatesWithAudio || false
    );
    const response = videoPrompts?.random();
    let videoDescription = response?.trim() || "";

    return videoDescription;
  }

  const getRandomStockImageByDescription = async (network, description, orientation = undefined, count = 11, page = undefined, accumulate = false) => {
    if (!orientation) orientation = orientationByNetwork?.[network]?.random();

    let images = [];
    if (!images || !images?.length || accumulate) {
      try {
        const pexelsImages = await getPexelsImages(count, undefined, description, orientation);
        accumulate ? images = [...images, ...pexelsImages] : images = pexelsImages;
      } catch (err) {}
    }
    if (!images || !images?.length || accumulate) {
      try {
        const pixabayImages = await getPixabayImages(count, undefined, description, orientation);
        accumulate ? images = [...images, ...pixabayImages] : images = pixabayImages;
      } catch (err) {}
    }
    if (!images || !images?.length || accumulate) {
      try {
        const unsplashImages = await getUnsplashImages(count, description, orientation);
        accumulate ? images = [...images, ...unsplashImages] : images = unsplashImages;
      } catch (err) {}
    }
    const randomImage = images.random();
    return randomImage?.large;
  }

  const getRandomGIFByDescription = async (network, description, orientation = undefined) => {
    const images = await getGiphyImages(3, undefined, description, orientation);
    const randomImage = images.random();
    return randomImage?.large;
  }

  const generateAIImageFromCopy = async (network, copy, media_product_type = undefined) => {
    let imageURL = "";
    try {
      const imagePrompt = await getAIImageDescription(copy);
      const aiResult = await getImages(
        imagePrompt,
        company?.ai_preferences?.image_aspect_ratio || getAIOrientationByNetworkMediaProductType(network, media_product_type),
        undefined,
        true
      );
      imageURL = await getLastHistoryAIImageURL(aiResult?.id);
    } catch (err) {}
    return imageURL;
  }

  const generateAIVideoFromCopy = async (network, copy, media_product_type = undefined) => {
    let videoURL = "";
    try {
      const videoPrompt = await getAIVideoDescription(copy);
      const aiResult = await getVideos(
        videoPrompt,
        company?.ai_preferences?.video_aspect_ratio || getAIOrientationByNetworkMediaProductType(network, media_product_type),
        undefined,
        null
      );
      if (aiResult && aiResult.status === 'completed' && Array.isArray(aiResult.urls) && aiResult.urls.length > 0) {
        videoURL = aiResult.urls[0];
      }
    } catch (err) {}
    return videoURL;
  }

  const generateStockImageFromCopy = async (network, copy, media_product_type = undefined) => {
    const imageDescription = await getStockImageDescription(copy);
    const image = await getRandomStockImageByDescription(
      network,
      imageDescription,
      getOrientationByNetworkMediaProductType(network, media_product_type)
    );
    return image;
  }

  const generateStockGIFFromCopy = async (network, copy, media_product_type = undefined) => {
    const imageDescription = await getStockImageDescription(copy);
    const image = await getRandomGIFByDescription(network, imageDescription);
    return image;
  }

  return {
    generateAIImageFromCopy,
    generateAIVideoFromCopy,
    generateStockImageFromCopy,
    generateStockGIFFromCopy,

    getRandomStockImageByDescription,
  }
}

export default useCreativeGenerator;
